// BUILD-046 increment 1b-ii: the searchable "Assigned to" filter of the toolbar's Filter popover (Team / Company and Assigned by Me; not My Tasks, not Shared Tasks).
//
// It is a CLIENT-SIDE display filter of the rows the page has already loaded, exactly like the Customer / Job filter (jobFilterModel.ts) and the Team department filter. It asks the server
// for nothing, so it can never offer, match or reveal a person, a task or a request beyond what the viewer was already given and already sees in the Assigned to cell.
//
//   - THE VALUE is the stable employee id, never a name. The name is only for reading (the chip, the option, the "Selected:" line).
//   - OPTIONS are the people PRESENT on the loaded rows, and only those. What counts as "present" is exactly what the row's Assigned to cell draws:
//       Team / Company   the owner of a claimed task; for a task nobody has taken (Anyone / Everyone) the people it was sent to (a group is not a person and is never an option);
//       Assigned by Me   Single: the assignee; Anyone, taken: the claimant; Anyone, untaken: nobody (the cell shows a group); Everyone: the live recipients the viewer may read
//                        (issuedRowModel.responsibleOf - the same function the cell calls, so the two cannot disagree).
//     A person the server did not give the viewer (a restricted row that names nobody, a recipient the viewer may not read) is not on the row, so is not an option and never matches.
//     A person with no employee id cannot be a value and is not an option. Two people with the same name and different ids stay two options (displayNames.disambiguatePeople adds the
//     full name, then an id suffix, to tell them apart). Each option is named as the cell names the person (First name + last initial, no test marker, no "(you)") with the department when a row
//     supplies one (the loaded rows carry none today, so none is shown).
//   - SEARCH ignores case and accents, needs every word typed, and matches the shown name, the stored full name and the department. At most eight matches, in a stable order (names that
//     begin with what was typed, then names with a word that does, then the rest; ties by name, then id). Nothing is listed until at least one character is typed, as in the job picker.
//   - MATCH RULE. A row matches when the selected person is among the people its Assigned to cell displays. An Everyone request matches when the person is one of its readable live
//     recipients; the WHOLE request stays (it is never split, and nothing about the other recipients changes), and the expansion marks the matched person with the words "Matches filter".
//     A request's hidden recipients are never mentioned.
//   - AND. The filter narrows the loaded rows together with Status, the department, the Customer / Job filter, the quick filters and the search. It runs before the search and the sort and never
//     reorders the rows that remain (a stable filter of an ordered list is the list's own order with rows left out).
//   - A person who is no longer on the loaded rows (the list was read again) stays chosen: the chip stays, the filter stays active, and an empty result reads as "no tasks match", not as a bug.
//
// Pure module: no React, no I/O. Runtime imports: the display helpers, the row helpers and the combobox key rules (all pure). Erasable TypeScript only.

import { disambiguatePeople, personDisplay, stripTestMarker } from './displayNames';
import { responsibleOf } from './issuedRowModel';
import { normalizeJobSearchText } from './jobFilterModel';
import { decideKey, optionId } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import type { IssuedRow } from './issuedRequestsModel';
import type { JobListKind } from './jobFilterModel';

// ───────────────────────── the filter value ─────────────────────────

export type AssigneeFilter =
  | { kind: 'any' }
  /** `employeeId` is the value. `label` is the displayed name ("Eddie", "Alex S."), `fullName` the whole name, `detail` the quiet extra line that told two look-alikes apart ('' when none). */
  | { kind: 'person'; employeeId: string; label: string; fullName: string; detail: string };

export const ANY_ASSIGNEE: AssigneeFilter = Object.freeze({ kind: 'any' } as const);

/** The visible name of the control and of its chip. */
export const ASSIGNEE_FILTER_LABEL = 'Assigned to';
/** The most matches shown at once. */
export const ASSIGNEE_FILTER_RESULT_LIMIT = 8;
/** What the expansion of an Everyone request says beside a recipient who matches the filter (words, so it never relies on colour). */
export const ASSIGNEE_MATCH_MARKER = 'Matches filter';
/** The rule in words: the note under the picker and the chip's title. It says nothing about recipients the viewer cannot read. */
export const ASSIGNEE_RULE_TEXT = 'Shows tasks that list this person under Assigned to. A request sent to several people stays whole when this person is one of its recipients.';
export const ASSIGNEE_PICKER_HINT = 'Type a name to see matches.';

export function assigneeFilterActive(filter: AssigneeFilter): boolean {
  return filter.kind === 'person';
}

/** The employee id the filter holds, or null for no filter. */
export function assigneeFilterId(filter: AssigneeFilter): string | null {
  return filter.kind === 'person' ? filter.employeeId : null;
}

// ───────────────────────── the people of a row ─────────────────────────

/** A person on a row, as the Assigned to cell draws them. `department` only where a row supplies one. */
export interface AssigneePerson {
  employeeId: string;
  /** The stored display name (the test marker is not part of what is shown). */
  name: string;
  department?: string;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

/** Each person once (by id), those with an id and a name only, in the order given. */
function distinctPeople(people: readonly AssigneePerson[]): AssigneePerson[] {
  const seen = new Set<string>();
  const out: AssigneePerson[] = [];
  for (const person of people) {
    const employeeId = text(person.employeeId);
    const name = text(person.name);
    if (employeeId === '' || name === '' || seen.has(employeeId)) continue;
    seen.add(employeeId);
    const department = stripTestMarker(person.department);
    out.push(department === '' ? { employeeId, name } : { employeeId, name, department });
  }
  return out;
}

/**
 * The people a Team / Company row's Assigned to cell shows: the owner of a claimed task, or the people an unclaimed task was sent to (the route's recipientSummary). A group is not a
 * person. A task that names no owner, or names nobody, gives nobody.
 */
export function peopleOfTask(row: unknown): AssigneePerson[] {
  if (row === null || typeof row !== 'object') return [];
  const r = row as { unclaimed?: unknown; ownerName?: unknown; ownerEmployeeId?: unknown; recipientSummary?: unknown };
  if (r.unclaimed !== true) return distinctPeople([{ employeeId: text(r.ownerEmployeeId), name: text(r.ownerName) }]);
  const entries: readonly unknown[] = Array.isArray(r.recipientSummary) ? r.recipientSummary : [];
  const people: AssigneePerson[] = [];
  for (const entry of entries) {
    if (entry === null || typeof entry !== 'object') continue;
    const e = entry as { name?: unknown; employeeId?: unknown; department?: unknown };
    people.push({ employeeId: text(e.employeeId), name: text(e.name), department: text(e.department) });
  }
  return distinctPeople(people);
}

/** The people an Assigned by Me row's Assigned to cell shows (issuedRowModel.responsibleOf): the assignee, the claimant, or the live recipients the viewer may read; never a group. */
export function peopleOfIssuedRow(row: IssuedRow): AssigneePerson[] {
  const cell = responsibleOf(row);
  return cell.kind === 'people' ? distinctPeople(cell.people.map((person) => ({ employeeId: person.employeeId, name: person.name }))) : [];
}

// ───────────────────────── the options ─────────────────────────

export interface AssigneeChoice {
  /** The stable id: the filter's value. */
  employeeId: string;
  /** The stored name (also searched). */
  name: string;
  /** What is shown: First name + last initial, or the first name. */
  label: string;
  /** The whole name, marker and "(you)" removed. */
  full: string;
  /** The department when a loaded row supplied one, else ''. */
  department: string;
  /** The quiet second line: the department, the full name when two look alike, an id suffix when even that is not enough; '' when none is needed. */
  detail: string;
  /** The option's accessible name: the full name, the department and the suffix when one was needed. */
  accessibleName: string;
  /** How many loaded rows list this person (informational). */
  count: number;
}

function collate(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * The people on ALL the given rows (every list the view has loaded), each once, in name order (then id). Built from the loaded rows only: a person who is on none of them is not here,
 * and nobody is added from anywhere else. The order does not depend on the order of the rows, so an option does not move when the list is read again.
 */
export function assigneeChoicesOf<T>(rowSets: readonly (readonly T[])[], peopleOf: (row: T) => readonly AssigneePerson[]): AssigneeChoice[] {
  const byId = new Map<string, { names: string[]; departments: string[]; count: number }>();
  for (const rows of rowSets) {
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const inThisRow = new Set<string>();
      for (const person of distinctPeople(peopleOf(row))) {
        let seen = byId.get(person.employeeId);
        if (seen === undefined) {
          seen = { names: [], departments: [], count: 0 };
          byId.set(person.employeeId, seen);
        }
        if (!seen.names.includes(person.name)) seen.names.push(person.name);
        if (person.department !== undefined && !seen.departments.includes(person.department)) seen.departments.push(person.department);
        if (!inThisRow.has(person.employeeId)) {
          inThisRow.add(person.employeeId);
          seen.count += 1;
        }
      }
    }
  }
  const people = [...byId.entries()].map(([employeeId, seen]) => {
    // One stored name per id, chosen without reference to row order: the smallest.
    const name = [...seen.names].sort(byCodeUnit)[0];
    const department = [...seen.departments].sort(byCodeUnit)[0] ?? '';
    return { employeeId, name, department, count: seen.count, display: personDisplay(name, { department }) };
  });
  people.sort((a, b) => collate(a.display.short, b.display.short) || collate(a.display.full, b.display.full) || byCodeUnit(a.employeeId, b.employeeId));
  const labels = disambiguatePeople(people.map((p) => ({ employeeId: p.employeeId, displayName: p.name, department: p.department })));
  return people.map((p, index): AssigneeChoice => ({
    employeeId: p.employeeId,
    name: p.name,
    label: labels[index].short,
    full: labels[index].full,
    department: labels[index].department,
    detail: labels[index].detail,
    accessibleName: labels[index].accessibleName,
    count: p.count,
  }));
}

/** Whether the chosen person is on a loaded row now (false after a re-read that no longer lists them; the filter then stays and the empty state says so). */
export function assigneePersonLoaded(options: readonly { employeeId: string }[], filter: AssigneeFilter): boolean {
  return filter.kind === 'person' && options.some((option) => option.employeeId === filter.employeeId);
}

// ───────────────────────── the search ─────────────────────────

/** Lower case, accents removed, white space collapsed: what a name and what is typed are compared as. */
export function normalizeAssigneeSearchText(raw: unknown): string {
  return normalizeJobSearchText(raw);
}

export type AssigneeSearchStatus = 'idle' | 'results' | 'none';

export interface AssigneeSearchOutcome {
  /** `idle`: nothing typed, so nothing is listed. `results`: at least one match. `none`: something was typed and nobody matches. */
  status: AssigneeSearchStatus;
  /** What was typed, trimmed (for "No people match ..."). */
  query: string;
  /** At most `limit` matches: names that begin with what was typed first, then names with a word that does, then the rest; each group by name, then id. */
  matches: AssigneeChoice[];
  /** How many people match in all. */
  total: number;
  /** More people match than are shown. */
  more: boolean;
}

function haystackOf(option: AssigneeChoice): string {
  return normalizeAssigneeSearchText([option.label, option.full, stripTestMarker(option.name), option.department].join(' '));
}

/**
 * The people that match what was typed, at most `limit` (default 8). Nothing is listed until at least one character is typed. Every word typed must be found in the shown name, the stored
 * full name or the department, ignoring case and accents, in any order. The result does not depend on the order of `options`.
 */
export function searchAssigneeOptions(options: readonly AssigneeChoice[], query: unknown, limit: number = ASSIGNEE_FILTER_RESULT_LIMIT): AssigneeSearchOutcome {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : ASSIGNEE_FILTER_RESULT_LIMIT;
  const typed = typeof query === 'string' ? query.replace(/\s+/g, ' ').trim() : '';
  const q = normalizeAssigneeSearchText(typed);
  if (q === '') return { status: 'idle', query: typed, matches: [], total: 0, more: false };
  const words = q.split(' ');
  const ranked: { option: AssigneeChoice; rank: number; key: string; fullKey: string }[] = [];
  for (const option of options) {
    const hay = haystackOf(option);
    if (!words.every((word) => hay.includes(word))) continue;
    const label = normalizeAssigneeSearchText(option.label);
    const full = normalizeAssigneeSearchText(option.full);
    const begins = label.startsWith(q) || full.startsWith(q);
    const wordBegins = [...label.split(' '), ...full.split(' ')].some((part) => part.startsWith(words[0]));
    ranked.push({ option, rank: begins ? 0 : wordBegins ? 1 : 2, key: label, fullKey: full });
  }
  ranked.sort((a, b) => a.rank - b.rank || collate(a.key, b.key) || collate(a.fullKey, b.fullKey) || byCodeUnit(a.option.employeeId, b.option.employeeId));
  const total = ranked.length;
  return { status: total === 0 ? 'none' : 'results', query: typed, matches: ranked.slice(0, cap).map((r) => r.option), total, more: total > cap };
}

export interface AssigneePickerView {
  status: AssigneeSearchStatus;
  /** "Showing 8 of 21. Keep typing to narrow." when more people match than are shown; null otherwise. */
  moreNote: string | null;
  /** "No people match ..." only after something was typed and nobody matches; null otherwise. */
  emptyText: string | null;
  /** The short hint while nothing is typed; null otherwise. */
  hint: string | null;
  /** The polite line for assistive technology; '' while idle. */
  announcement: string;
}

export function describeAssigneePicker(outcome: AssigneeSearchOutcome, limit: number = ASSIGNEE_FILTER_RESULT_LIMIT): AssigneePickerView {
  const cap = Number.isInteger(limit) && limit > 0 ? limit : ASSIGNEE_FILTER_RESULT_LIMIT;
  if (outcome.status === 'idle') return { status: 'idle', moreNote: null, emptyText: null, hint: ASSIGNEE_PICKER_HINT, announcement: '' };
  if (outcome.status === 'none') return { status: 'none', moreNote: null, emptyText: `No people match "${outcome.query}".`, hint: null, announcement: 'No people match.' };
  const shown = outcome.matches.length;
  const moreNote = outcome.more ? `Showing ${cap} of ${outcome.total}. Keep typing to narrow.` : null;
  const found = `${shown} ${shown === 1 ? 'person' : 'people'} found.`;
  return { status: 'results', moreNote, emptyText: null, hint: null, announcement: `${moreNote !== null ? `${moreNote} ` : `${found} `}Use the arrow keys, then Enter to choose.` };
}

// ───────────────────────── keys ─────────────────────────

export interface AssigneeKeyDecision {
  /** The key was handled here: the caller prevents the browser default. */
  handled: boolean;
  /** Stop the key from reaching the popover (the Escape that closed the list). */
  stop: boolean;
  state: ComboState;
  /** The employee id Enter chose, or null. */
  choose: string | null;
}

/**
 * What a key does in the picker: the people combobox rules for a single choice. ArrowDown and ArrowUp move (wrapping), Home and End jump, Enter chooses the highlighted person, Escape
 * closes the list (with it already closed the key is left to the popover), and Tab only closes the list and moves on: this is a FILTER, not an assignment field, so Tab NEVER chooses.
 */
export function decideAssigneeKey(key: string, combo: ComboState, options: readonly { employeeId: string }[]): AssigneeKeyDecision {
  const decision = decideKey(key, combo, options, 0);
  return { handled: decision.handled, stop: decision.stop, state: decision.state, choose: decision.choose };
}

/** A stable, id-safe element id for one option. */
export function assigneeOptionId(baseId: string, employeeId: string): string {
  return optionId(baseId, employeeId);
}

// ───────────────────────── choosing, matching, filtering ─────────────────────────

/** The filter a chosen option stands for: the STABLE ID is the value. A choice without a usable id or name gives null (nothing is guessed). */
export function assigneeFilterFromChoice(choice: unknown): AssigneeFilter | null {
  if (choice === null || typeof choice !== 'object') return null;
  const c = choice as { employeeId?: unknown; label?: unknown; full?: unknown; detail?: unknown };
  const employeeId = text(c.employeeId);
  const label = text(c.label);
  if (employeeId === '' || label === '') return null;
  const full = text(c.full);
  return { kind: 'person', employeeId, label, fullName: full === '' ? label : full, detail: text(c.detail) };
}

/** True when one of the people is the chosen person. No filter matches everything. */
export function personMatchesAssignee(filter: AssigneeFilter, people: readonly { employeeId: string }[]): boolean {
  if (filter.kind === 'any') return true;
  return people.some((person) => person.employeeId === filter.employeeId);
}

/** True when this id is the chosen person (the expansion of an Everyone request marks that recipient). Never true without a filter. */
export function isAssigneeMatch(filter: AssigneeFilter, employeeId: string): boolean {
  return filter.kind === 'person' && employeeId !== '' && employeeId === filter.employeeId;
}

/**
 * Keeps the rows whose Assigned to cell shows the chosen person. `any` keeps every row (the same list object). Rows keep their order and are kept WHOLE: a row with several people is
 * never split, and nothing about it changes.
 */
export function filterByAssignee<T>(rows: readonly T[], filter: AssigneeFilter, peopleOf: (row: T) => readonly AssigneePerson[]): readonly T[] {
  if (filter.kind === 'any') return rows;
  return rows.filter((row) => personMatchesAssignee(filter, peopleOf(row)));
}

// ───────────────────────── one filter per view ─────────────────────────

/** The two views that can have an Assigned to filter (Company is the Team view), and the filter each holds. */
export type AssigneeFilterView = 'team-work' | 'issued';
export type AssigneeFilters = Readonly<Record<AssigneeFilterView, AssigneeFilter>>;

export function initialAssigneeFilters(): AssigneeFilters {
  return { 'team-work': ANY_ASSIGNEE, issued: ANY_ASSIGNEE };
}

export function setAssigneeFilter(filters: AssigneeFilters, view: AssigneeFilterView, next: AssigneeFilter): AssigneeFilters {
  const current = filters[view];
  if (current === undefined) return filters;
  if (current.kind === next.kind && assigneeFilterId(current) === assigneeFilterId(next)) return filters;
  return { ...filters, [view]: next };
}

/** Removing the chip clears ONLY this view's Assigned to filter. Already off: the same object. */
export function removeAssigneeFilter(filters: AssigneeFilters, view: AssigneeFilterView): AssigneeFilters {
  return setAssigneeFilter(filters, view, ANY_ASSIGNEE);
}

// ───────────────────────── words ─────────────────────────

/** What the filter is, in words, for the panel: "All people" or the person's displayed name. */
export function assigneeFilterSummary(filter: AssigneeFilter): string {
  return filter.kind === 'any' ? 'All people' : filter.label;
}

export interface AssigneeFilterChipView {
  /** "Assigned to: Eddie". */
  text: string;
  /** The Remove button's accessible name. */
  removeLabel: string;
  /** The chip's title: the whole name (and the quiet detail) and the match rule. */
  title: string;
}

/** The removable chip: none for no filter, so the chip always means a filter is on. */
export function describeAssigneeFilterChip(filter: AssigneeFilter): AssigneeFilterChipView | null {
  if (filter.kind === 'any') return null;
  // The quiet detail already starts with the full name when two people look alike ("Sam Kim · ID …am-1"); then it stands alone.
  const who = filter.detail !== '' && filter.detail.startsWith(filter.fullName) ? filter.detail : [filter.fullName, filter.detail].filter((part) => part !== '').join(' \u00b7 ');
  return { text: `${ASSIGNEE_FILTER_LABEL}: ${filter.label}`, removeLabel: `Remove ${ASSIGNEE_FILTER_LABEL} filter`, title: `${ASSIGNEE_FILTER_LABEL}: ${who}. ${ASSIGNEE_RULE_TEXT}` };
}

/**
 * The sentence under the picker. It names where the people come from, never claims to search everyone, and says that loading more can add other people where a list is read page by page
 * (Team / Company Completed, Assigned by Me). Same wording style as the job picker's.
 */
export function assigneeFilterScopeText(kind: JobListKind, loadedCount: number, subject: string = 'tasks'): string {
  const n = Number.isInteger(loadedCount) && loadedCount > 0 ? loadedCount : 0;
  const singular = subject.replace(/s$/, '');
  const what = `${n} ${n === 1 ? singular : subject}`;
  if (kind === 'paged') return `People shown are those on the ${what} loaded so far. Loading more can add other people; this does not search every ${singular}.`;
  if (kind === 'open-only') return `People shown are those on the ${what} loaded here. Completed tasks are included once Completed is opened.`;
  return `People shown are those on the ${what} loaded in this list.`;
}

export interface AssigneeFilterEmptyView {
  headline: string;
  detail: string;
  clearLabel: string;
}

export interface AssigneeEmptyInput {
  /** The other things that also narrow the list ("Status: Open", "Customer / Job: Smith Residence", "search"); empty when none. */
  also: readonly string[];
  /** False for a list read page by page: only the loaded rows were checked. */
  complete: boolean;
  /** Whether the chosen person is on a loaded row now (assigneePersonLoaded). */
  personLoaded: boolean;
  noun?: 'tasks' | 'requests';
}

/**
 * The empty state when a person is chosen and no row is left. It names the filter ("No tasks match Assigned to: Eddie with the other filters."), says which other filters also apply, says so
 * when the person is not on the loaded rows any more, and offers to clear the filter. It is never the generic "no tasks".
 */
export function describeAssigneeFilterEmpty(filter: AssigneeFilter, input: AssigneeEmptyInput): AssigneeFilterEmptyView | null {
  const chip = describeAssigneeFilterChip(filter);
  if (chip === null || filter.kind !== 'person') return null;
  const noun = input.noun ?? 'tasks';
  const also = input.also.map((item) => item.trim()).filter((item) => item !== '');
  const parts: string[] = [];
  if (!input.personLoaded) parts.push(`${filter.label} is not on the ${noun} loaded now.`);
  if (also.length > 0) parts.push(`Also applying: ${also.join('; ')}.`);
  parts.push(input.complete ? `Clear the ${ASSIGNEE_FILTER_LABEL} filter to see the ${noun} again.` : `Only the ${noun} loaded so far were checked. Load more, or clear the ${ASSIGNEE_FILTER_LABEL} filter.`);
  // A displayed name can end with a period ("Dana D."), so the sentence's own period is added only when the words do not already end with one.
  const lead = `No ${noun} match ${chip.text}${also.length > 0 ? ' with the other filters' : ''}`;
  return { headline: lead.endsWith('.') ? lead : `${lead}.`, detail: parts.join(' '), clearLabel: `Clear ${ASSIGNEE_FILTER_LABEL} filter` };
}
